import { useNavigate } from 'react-router-dom'
import FilterableTableView from '../components/FilterableTableView'
import { useEffect, useState, useRef } from 'react'
import { fetchWithLanguage } from '../../util/fetchFunction'
import { useTranslation } from 'react-i18next'
import { languageUtil } from '../../util/languageUtils'
import { Button } from '../../shared/components/Button'
import { API_URL } from '../../constants'
import { LoadingOverlay } from '../../shared/components/Loading'

const Reports = () => {
    const [donationsData, setDonationsData] = useState([])
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadingExport, setLoadingExport] = useState(false)

    const filterableTableRef = useRef(null)
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { language } = languageUtil()

    useEffect(() => {
        const fetchDonations = async () => {
            await fetchWithLanguage(
                `admin/manage-donations`,
                setDonationsData,
                setError,
                setLoading,
                {
                    token: true,
                }
            )
        }

        fetchDonations()
    }, [language])

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token')

            setLoadingExport(true)

            const response = await fetch(`${API_URL}/admin/billings/export`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })

            if (!response.ok) {
                throw new Error(`Export failed: ${response.status} ${response.statusText}`)
            }

            const blob = await response.blob()

            if (!blob || blob.size === 0) {
                alert(t('donations_table.statuses.no_data'))
                return
            }

            let filename = `donations_export_${new Date().toISOString().split('T')[0]}.xlsx`

            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)

            link.setAttribute('href', url)
            link.setAttribute('download', filename)
            link.style.visibility = 'hidden'

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            setTimeout(() => {
                URL.revokeObjectURL(url)
            }, 100)
        } catch (error) {
            console.error('Export error:', error)
            alert(t('donations_table.statuses.export_failed') || `Export failed: ${error.message}`)
        } finally {
            setLoadingExport(false)
        }
    }

    return (
        <div className='w-full p-6 bg-white border shadow-md rounded-xl'>
            <>
                {(loadingExport || loading) && <LoadingOverlay />}
                <div className='flex items-center justify-between mb-6'>
                    <h1 className='text-2xl font-bold text-gray-800'>
                        {t('navigation_admin.donations.main')}
                    </h1>
                    <div className='flex gap-2'>
                        <Button onClick={handleExport}>{t('status.export')}</Button>
                    </div>
                </div>

                <FilterableTableView
                    apiEndpoint={'/manage-donations'}
                    ref={filterableTableRef}
                    onRowClick={row => navigate(`/admin/auth/donations/${row.id}`)}
                    columns={[
                        {
                            key: 'donor_name',
                            label: t('donations_table.columns.donor_name'),
                            sortable: true,
                        },
                        {
                            key: 'total_amount',
                            label: t('donations_table.columns.total_amount'),
                            sortable: true,
                        },
                        {
                            key: 'transaction_ref',
                            label: t('donations_table.columns.transaction_ref'),
                        },
                        {
                            key: 'date',
                            label: t('donations_table.columns.date'),
                            sortable: true,
                        },
                        {
                            key: 'gift_aid',
                            label: t('donations_table.columns.gift_aid'),
                            sortable: true,
                        },
                        {
                            key: 'status',
                            label: t('donations_table.columns.status'),
                            sortable: true,
                        },
                    ]}
                    data={donationsData}
                    searchPlaceholder={t('search.donor')}
                    searchKeys={['donor_name']}
                    filters={[
                        {
                            key: 'status',
                            label: t('donations_table.filters.status.label'),
                            type: 'select',
                            options: [
                                {
                                    label: t('donations_table.filters.status.options.all'),
                                    value: '',
                                },
                                {
                                    label: t('donations_table.filters.status.options.pending'),
                                    value: 'pending',
                                },
                                {
                                    label: t('donations_table.filters.status.options.paid'),
                                    value: 'paid',
                                },
                            ],
                        },
                        {
                            key: 'date',
                            label: t('donations_table.filters.date_range.label'),
                            type: 'date-range',
                            startKey: 'start_date',
                            endKey: 'end_date',
                            startPlaceholder: t(
                                'donations_table.filters.date_range.start_placeholder'
                            ),
                            endPlaceholder: t('donations_table.filters.date_range.end_placeholder'),
                        },
                        {
                            key: 'total_amount',
                            label: t('donations_table.filters.amount_range.label'),
                            type: 'range',
                            minKey: 'min_amount',
                            maxKey: 'max_amount',
                            minPlaceholder: t(
                                'donations_table.filters.amount_range.min_placeholder'
                            ),
                            maxPlaceholder: t(
                                'donations_table.filters.amount_range.max_placeholder'
                            ),
                        },
                        {
                            key: 'gift_aid',
                            label: t('donations_table.filters.gift_aid.label'),
                            type: 'select',
                            options: [
                                {
                                    label: t('donations_table.filters.gift_aid.options.any'),
                                    value: '',
                                },
                                {
                                    label: t('donations_table.filters.gift_aid.options.yes'),
                                    value: '1',
                                },
                                {
                                    label: t('donations_table.filters.gift_aid.options.no'),
                                    value: '0',
                                },
                            ],
                        },
                    ]}
                />
            </>
        </div>
    )
}

export default Reports
